import { useCallback } from "react";
import { AppointmentDto } from "../../../../api-client";
import useStore from "../../../../helpers/useStore";
import { CalendarContext } from "../../../../stores/Calendar/calendar.provider";

/** Backend sentinel for "no gap" in the legacy `mergeAppointmentGaps` string. */
const NO_GAP = "00:00.00";

export interface MergeAppointmentGapsWarning {
  /**
   * Inspect the survivor appointment a schedule/reschedule returned and, when
   * its treatments no longer sit back to back, surface the gap warning. No-ops
   * when the visit is contiguous.
   */
  showVisitGapWarningIfPresent: (appointment: AppointmentDto | null | undefined) => void;
  /**
   * Legacy entry point for results that carried the backend's
   * `mergeAppointmentGaps` string. Only the deprecated
   * `calendar.updateAppointment` envelope ever did — every scheduler verb
   * returns a bare appointment DTO — so this falls through to the DTO check.
   */
  showMergeGapWarningIfPresent: (
    result: (AppointmentDto & { mergeAppointmentGaps?: string }) | null | undefined,
  ) => void;
}

/**
 * The idle time inside a visit, in minutes: the appointment's span minus the
 * time actually treated. Mirrors the backend's `getGapInTimeSlots`.
 *
 * Zero for a contiguous visit, and for parallel slots (two therapists at once),
 * where the summed durations exceed the span and the difference goes negative.
 */
export const visitGapMinutes = (appointment: AppointmentDto | null | undefined): number => {
  const slots = appointment?.timeSlots ?? [];
  if (!appointment?.start || !appointment?.end || slots.length < 2) return 0;

  const spanMs = new Date(appointment.end).getTime() - new Date(appointment.start).getTime();
  const treatedMs = slots.reduce((sum, slot) => {
    if (!slot.start || !slot.end) return sum;
    return sum + (new Date(slot.end).getTime() - new Date(slot.start).getTime());
  }, 0);

  const gapMs = spanMs - treatedMs;
  return gapMs > 0 ? Math.round(gapMs / 60000) : 0;
};

/** "1 Std. 15 Min." / "30 Min." */
const formatGap = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest} Min.`;
  return rest ? `${hours} Std. ${rest} Min.` : `${hours} Std.`;
};

/**
 * The visit-gap warning shared by every calendar shell.
 *
 * Moving ONE treatment of a multi-slot visit leaves the patient waiting between
 * their treatments, which is easy to do by accident and invisible afterwards —
 * so the move goes through and this reports the gap it opened.
 *
 * The gap is derived from the survivor appointment the mutation already
 * returns, NOT from the backend. The `mergeAppointmentGaps` field this hook
 * used to read never arrives: the scheduler controllers return a bare
 * `AppointmentDto` and drop the service's `mergeGap`, and that value is only
 * computed during a same-day MERGE anyway — `mergeNormalAppointments` returns
 * early when the patient has a single appointment that day, which is exactly
 * the case here. Client-side the check is one subtraction and covers every
 * path uniformly.
 *
 * The dialog is hosted shell-wide by CalendarActionDialogs, so both the legacy
 * Scheduler and MobiliCal show it without their own wiring.
 */
export const useMergeAppointmentGapsWarning = (): MergeAppointmentGapsWarning => {
  const CalendarStore = useStore(CalendarContext);

  const showVisitGapWarningIfPresent = useCallback(
    (appointment: AppointmentDto | null | undefined) => {
      const gap = visitGapMinutes(appointment);
      if (!gap) return;
      CalendarStore.openCalendarDialog({
        kind: "notice",
        title: "Lücke im Besuch",
        message:
          `Die Behandlungen dieses Besuchs liegen nicht mehr direkt hintereinander. ` +
          `Der Patient wartet ${formatGap(gap)} zwischen den Behandlungen.`,
      });
    },
    [CalendarStore],
  );

  const showMergeGapWarningIfPresent = useCallback(
    (result: (AppointmentDto & { mergeAppointmentGaps?: string }) | null | undefined) => {
      const gap = result?.mergeAppointmentGaps;
      if (gap && gap !== NO_GAP) {
        CalendarStore.openCalendarDialog({
          kind: "notice",
          title: "Lücke im Besuch",
          message: `Zwischen dem Besuch und einem Termin besteht eine Lücke von ${gap}.`,
        });
        return;
      }
      showVisitGapWarningIfPresent(result);
    },
    [CalendarStore, showVisitGapWarningIfPresent],
  );

  return { showVisitGapWarningIfPresent, showMergeGapWarningIfPresent };
};
